import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import { useToast, ToastContainer } from '../../components/ui/Toast';
import { getGPT5ChatCompletion, generateRecipeSuggestions, generateMenuSuggestions, generateShoppingList, getStreamingChatCompletion } from '../../services/openai';

// Import components
import ChatMessage from './components/ChatMessage';
import QuickActions from './components/QuickActions';
import RecipeSuggestionCard from './components/RecipeSuggestionCard';
import MenuSuggestionCard from './components/MenuSuggestionCard';

const AIAssistant = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError, showInfo } = useToast();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // State management
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [activeFeature, setActiveFeature] = useState('chat'); // 'chat', 'recipes', 'menu', 'shopping'
  const [suggestions, setSuggestions] = useState(null);

  // Current user mock data
  const currentUser = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    avatar: "https://images.unsplash.com/photo-1734991032476-bceab8383a59",
    avatarAlt: "Professional headshot of woman with shoulder-length brown hair in business attire"
  };

  // Initial welcome message
  useEffect(() => {
    setMessages([
      {
        id: 1,
        type: 'assistant',
        content: "Hello! I'm your AI culinary assistant. I can help you with recipe suggestions, dinner party planning, menu creation, shopping lists, and more. What would you like to explore today?",
        timestamp: new Date()?.toISOString(),
        features: ['recipes', 'menu', 'shopping', 'general']
      }
    ]);
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // Handle sending messages
  const handleSendMessage = async (message = inputValue, messageType = 'user') => {
    if (!message?.trim() && messageType === 'user') return;

    const newMessage = {
      id: Date.now(),
      type: messageType,
      content: message,
      timestamp: new Date()?.toISOString()
    };

    setMessages(prev => [...prev, newMessage]);
    if (messageType === 'user') {
      setInputValue('');
    }
    setIsLoading(true);

    try {
      let response;
      
      switch (activeFeature) {
        case 'recipes':
          await handleRecipeGeneration(message);
          break;
        case 'menu':
          await handleMenuGeneration(message);
          break;
        case 'shopping':
          await handleShoppingListGeneration(message);
          break;
        default:
          response = await getGPT5ChatCompletion(message);
          const assistantMessage = {
            id: Date.now() + 1,
            type: 'assistant',
            content: response,
            timestamp: new Date()?.toISOString()
          };
          setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to get response. Please try again.');
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again or contact support if the issue persists.",
        timestamp: new Date()?.toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle streaming responses
  const handleStreamingChat = async (message) => {
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()?.toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setStreamingMessage('');

    try {
      let fullResponse = '';
      
      await getStreamingChatCompletion(message, (chunk) => {
        fullResponse += chunk;
        setStreamingMessage(fullResponse);
      });

      // Add final message
      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: fullResponse,
        timestamp: new Date()?.toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setStreamingMessage('');
    } catch (error) {
      console.error('Error in streaming chat:', error);
      showError('Failed to get streaming response.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle recipe generation
  const handleRecipeGeneration = async (query) => {
    try {
      // Extract ingredients from query (simple approach)
      const ingredients = query?.match(/\b(?:chicken|beef|pasta|rice|tomato|onion|garlic|cheese|lettuce|spinach|carrots|potatoes|salmon|tuna|bread|milk|eggs|butter|oil|salt|pepper)\b/gi) || [];
      
      if (ingredients?.length === 0) {
        // If no ingredients detected, ask for clarification
        const assistantMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: "I'd love to help you with recipes! Could you tell me what ingredients you have available, or what type of cuisine you're interested in?",
          timestamp: new Date()?.toISOString()
        };
        setMessages(prev => [...prev, assistantMessage]);
        return;
      }

      const recipes = await generateRecipeSuggestions(ingredients, { query });
      setSuggestions({ type: 'recipes', data: recipes });
      
      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I found some great recipes using ${ingredients?.join(', ')}! Here are my suggestions:`,
        timestamp: new Date()?.toISOString(),
        suggestions: recipes
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error generating recipes:', error);
      showError('Failed to generate recipes. Please try again.');
    }
  };

  // Handle menu generation
  const handleMenuGeneration = async (query) => {
    try {
      // Parse party details from query (simplified)
      const guestCount = query?.match(/(\d+)\s*(?:guests?|people)/i)?.[1] || 6;
      const partyDetails = {
        guestCount: parseInt(guestCount),
        theme: query?.toLowerCase()?.includes('italian') ? 'Italian' : 
               query?.toLowerCase()?.includes('asian') ? 'Asian' : 
               query?.toLowerCase()?.includes('mexican') ? 'Mexican' : 'General',
        budget: query?.toLowerCase()?.includes('budget') ? 'Budget-friendly' : 'Moderate'
      };

      const menu = await generateMenuSuggestions(partyDetails);
      setSuggestions({ type: 'menu', data: menu });
      
      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I've created a dinner party menu for ${guestCount} guests! Here's what I suggest:`,
        timestamp: new Date()?.toISOString(),
        suggestions: menu
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error generating menu:', error);
      showError('Failed to generate menu. Please try again.');
    }
  };

  // Handle shopping list generation
  const handleShoppingListGeneration = async (query) => {
    try {
      // For demo, use some sample dishes
      const sampleDishes = [
        { name: 'Spaghetti Carbonara' },
        { name: 'Caesar Salad' },
        { name: 'Tiramisu' }
      ];
      
      const guestCount = query?.match(/(\d+)\s*(?:guests?|people)/i)?.[1] || 6;
      const shoppingList = await generateShoppingList(sampleDishes, parseInt(guestCount));
      setSuggestions({ type: 'shopping', data: shoppingList });
      
      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `Here's your organized shopping list for ${guestCount} guests:`,
        timestamp: new Date()?.toISOString(),
        suggestions: shoppingList
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error generating shopping list:', error);
      showError('Failed to generate shopping list. Please try again.');
    }
  };

  // Handle image upload and analysis
  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!file?.type?.startsWith('image/')) {
      showError('Please upload an image file.');
      return;
    }

    setIsLoading(true);

    try {
      // Create object URL for the image
      const imageUrl = URL.createObjectURL(file);
      
      // Add user message with image
      const imageMessage = {
        id: Date.now(),
        type: 'user',
        content: 'Please analyze this food image',
        timestamp: new Date()?.toISOString(),
        image: imageUrl,
        imageAlt: 'Uploaded food image for analysis'
      };
      setMessages(prev => [...prev, imageMessage]);

      // Analyze the image (note: this would need a proper image URL for OpenAI API)
      // For demo purposes, we'll show a mock response
      const mockAnalysis = {
        analysis: "I can see this is a delicious-looking dish! Based on the visual elements, this appears to be a pasta dish with what looks like a creamy sauce, possibly carbonara or alfredo. I can identify ingredients like pasta, cheese, and herbs. Would you like me to suggest similar recipes or provide cooking tips for this type of dish?",
        analysisType: 'recipe'
      };

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: mockAnalysis?.analysis,
        timestamp: new Date()?.toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Clean up object URL
      URL.revokeObjectURL(imageUrl);
    } catch (error) {
      console.error('Error analyzing image:', error);
      showError('Failed to analyze image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle quick actions
  const handleQuickAction = (action) => {
    switch (action) {
      case 'recipe-suggestions': setActiveFeature('recipes');
        handleSendMessage('I need recipe suggestions based on ingredients I have', 'user');
        break;
      case 'menu-planning': setActiveFeature('menu');
        handleSendMessage('Help me plan a dinner party menu for 6 guests', 'user');
        break;
      case 'shopping-list': setActiveFeature('shopping');
        handleSendMessage('Generate a shopping list for my dinner party', 'user');
        break;
      case 'dietary-advice': setActiveFeature('chat');
        handleSendMessage('I need advice about dietary restrictions for my dinner party', 'user');
        break;
      case 'wine-pairing': setActiveFeature('chat');
        handleSendMessage('Help me choose wine pairings for my dinner menu', 'user');
        break;
      case 'cooking-tips': setActiveFeature('chat');
        handleSendMessage('Give me cooking tips for hosting a dinner party', 'user');
        break;
      default:
        break;
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e?.preventDefault();
    if (activeFeature === 'chat' && inputValue?.includes('stream')) {
      handleStreamingChat(inputValue);
    } else {
      handleSendMessage();
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} onLogout={handleLogout} />
      <main className="pt-16 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center">
                <Icon name="Bot" size={32} className="mr-3 text-primary" />
                AI Culinary Assistant
              </h1>
              <p className="text-muted-foreground mt-2">
                Get personalized recipe suggestions, menu planning, and cooking advice
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef?.current?.click()}
                disabled={isLoading}
              >
                <Icon name="Camera" size={16} className="mr-2" />
                Analyze Image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Feature Toggle */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { key: 'chat', label: 'General Chat', icon: 'MessageCircle' },
              { key: 'recipes', label: 'Recipe Ideas', icon: 'ChefHat' },
              { key: 'menu', label: 'Menu Planning', icon: 'Calendar' },
              { key: 'shopping', label: 'Shopping Lists', icon: 'ShoppingCart' }
            ]?.map((feature) => (
              <Button
                key={feature?.key}
                variant={activeFeature === feature?.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFeature(feature?.key)}
              >
                <Icon name={feature?.icon} size={16} className="mr-2" />
                {feature?.label}
              </Button>
            ))}
          </div>

          {/* Quick Actions */}
          {messages?.length <= 1 && (
            <QuickActions onAction={handleQuickAction} />
          )}

          {/* Chat Messages */}
          <div className="bg-card rounded-lg border min-h-[400px] max-h-[600px] overflow-y-auto mb-6">
            <div className="p-6 space-y-4">
              {messages?.map((message) => (
                <ChatMessage
                  key={message?.id}
                  message={message}
                  currentUser={currentUser}
                />
              ))}
              
              {/* Streaming message */}
              {streamingMessage && (
                <ChatMessage
                  message={{
                    id: 'streaming',
                    type: 'assistant',
                    content: streamingMessage,
                    timestamp: new Date()?.toISOString()
                  }}
                  currentUser={currentUser}
                  isStreaming={true}
                />
              )}
              
              {/* Loading indicator */}
              {isLoading && !streamingMessage && (
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Bot" size={16} className="text-primary" />
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Suggestions Display */}
          {suggestions && (
            <div className="mb-6">
              {suggestions?.type === 'recipes' && suggestions?.data?.recipes && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {suggestions?.data?.recipes?.map((recipe, index) => (
                    <RecipeSuggestionCard
                      key={index}
                      recipe={recipe}
                      onSave={() => showSuccess(`${recipe?.name} saved to your recipe library!`)}
                    />
                  ))}
                </div>
              )}
              
              {suggestions?.type === 'menu' && suggestions?.data?.menu && (
                <MenuSuggestionCard
                  menu={suggestions?.data}
                  onSave={() => showSuccess('Menu saved to your dinner planning!')}
                />
              )}
              
              {suggestions?.type === 'shopping' && suggestions?.data?.categories && (
                <div className="bg-card rounded-lg border p-6">
                  <h3 className="text-lg font-semibold mb-4">Shopping List</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(suggestions?.data?.categories)?.map(([category, items]) => (
                      <div key={category} className="space-y-2">
                        <h4 className="font-medium capitalize text-primary">{category}</h4>
                        <ul className="space-y-1">
                          {items?.map((item, index) => (
                            <li key={index} className="text-sm text-muted-foreground flex items-center">
                              <Icon name="Check" size={12} className="mr-2 text-green-500" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  {suggestions?.data?.estimatedCost && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Estimated cost: {suggestions?.data?.estimatedCost}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Input Form */}
          <form onSubmit={handleSubmit} className="bg-card rounded-lg border p-4">
            <div className="flex space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e?.target?.value)}
                  placeholder={`Ask about ${activeFeature === 'recipes' ? 'recipes and ingredients' : 
                                            activeFeature === 'menu' ? 'dinner party menus' : 
                                            activeFeature === 'shopping'? 'shopping lists' : 'cooking, recipes, or dinner planning'}...`}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  disabled={isLoading}
                />
              </div>
              <Button type="submit" disabled={isLoading || !inputValue?.trim()}>
                <Icon name="Send" size={16} />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => setInputValue('What are some easy appetizers for a dinner party?')}
                className="text-xs px-3 py-1 bg-muted hover:bg-muted/80 rounded-full text-muted-foreground hover:text-foreground transition-colors"
              >
                Appetizer ideas
              </button>
              <button
                type="button"
                onClick={() => setInputValue('Help me pair wines with Italian food')}
                className="text-xs px-3 py-1 bg-muted hover:bg-muted/80 rounded-full text-muted-foreground hover:text-foreground transition-colors"
              >
                Wine pairing
              </button>
              <button
                type="button"
                onClick={() => setInputValue('What can I make with chicken, rice, and vegetables?')}
                className="text-xs px-3 py-1 bg-muted hover:bg-muted/80 rounded-full text-muted-foreground hover:text-foreground transition-colors"
              >
                Recipe ideas
              </button>
            </div>
          </form>
        </div>
      </main>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default AIAssistant;